/**
 * Button Reply Utilities
 * 
 * Utility functions for handling reply buttons in chat messages.
 * Reply buttons send a message back to the chat instead of navigating to a URL.
 * 
 * Syntax:
 * - [btn:Label](reply:BASE64MESSAGE) - Sends decoded base64 message
 * - [btn:Label](reply:plain text) - Sends the plain-text payload as the message
 * - [btn:Label](reply:) - Sends the label as the message (fallback)
 * 
 * Follows ADR principles:
 * - ADR-003: High Cohesion - Single responsibility: handle reply button logic
 * - ADR-005: Information Hiding - Encapsulates base64 encoding/decoding details
 */

/**
 * The prefix used to identify reply button URLs
 */
export const REPLY_URL_PREFIX = 'reply:';

/**
 * Checks if a URL is a reply button URL
 * 
 * @param url - The URL to check
 * @returns True if the URL starts with 'reply:', false otherwise
 * 
 * @example
 * ```ts
 * isReplyButton('reply:SGVsbG8=') // true
 * isReplyButton('reply:') // true
 * isReplyButton('https://example.com') // false
 * ```
 */
export function isReplyButton(url: string): boolean {
  return url.startsWith(REPLY_URL_PREFIX);
}

/**
 * Decodes a base64 string to UTF-8 text
 * Handles Unicode characters (including Danish æ, ø, å)
 * 
 * @param base64 - The base64 encoded string
 * @returns The decoded UTF-8 string, or null if decoding fails
 */
function decodeBase64(base64: string): string | null {
  try {
    // Handle empty string
    if (!base64) {
      return null;
    }
    
    // Decode base64 to binary string, then convert to UTF-8
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  } catch (error) {
    // Return null if base64 decoding fails
    return null;
  }
}

/**
 * Extracts and decodes the reply message from a reply button URL
 * Falls back to the provided label if no valid base64 message is found
 * 
 * @param url - The reply button URL (e.g., 'reply:SGVsbG8=' or 'reply:')
 * @param fallbackLabel - The button label to use as fallback
 * @returns The message to send back to the chat
 * 
 * @example
 * ```ts
 * // With base64 encoded message
 * decodeReplyMessage('reply:SGVsbG8gV29ybGQ=', 'Click') // 'Hello World'
 * 
 * // With Danish characters (æ, ø, å)
 * decodeReplyMessage('reply:SmEsIGplZyB2aWwgZ2VybmU=', 'Ja') // 'Ja, jeg vil gerne'
 * 
 * // Empty base64, falls back to label
 * decodeReplyMessage('reply:', 'Yes') // 'Yes'
 * 
 * // Plain-text payload
 * decodeReplyMessage('reply:Jeg vil starte en AI-afklaring', 'Start') // 'Jeg vil starte en AI-afklaring'
 * ```
 */
function normalizeBase64Payload(payload: string): string {
  return payload.replace(/=+$/, '');
}

function isValidBase64Payload(payload: string, decodedMessage: string): boolean {
  return normalizeBase64Payload(encodeReplyMessage(decodedMessage)) === normalizeBase64Payload(payload);
}

export function decodeReplyMessage(url: string, fallbackLabel: string): string {
  const payload = url.slice(REPLY_URL_PREFIX.length);

  if (!payload.trim()) {
    return fallbackLabel;
  }

  const decodedMessage = decodeBase64(payload);
  if (decodedMessage !== null && isValidBase64Payload(payload, decodedMessage)) {
    return decodedMessage;
  }

  return payload;
}

/**
 * Encodes a UTF-8 string to base64
 * Useful for backend implementations generating reply button URLs
 * 
 * @param text - The text to encode
 * @returns The base64 encoded string
 * 
 * @example
 * ```ts
 * encodeReplyMessage('Hello World') // 'SGVsbG8gV29ybGQ='
 * encodeReplyMessage('Ja, jeg vil gerne') // 'SmEsIGplZyB2aWwgZ2VybmU='
 * ```
 */
export function encodeReplyMessage(text: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(text);
  const binaryString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binaryString);
}

